import { Injectable } from '@angular/core';
import {
	Observable,
	catchError,
	map,
	throwError,
	combineLatest,
	forkJoin,
	switchMap,
	asyncScheduler,
	filter,
	throttleTime,
	tap
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import {
	ApolloQueryErrorMapper,
	ApolloQueryError,
	NodeUpdateInput,
	AudioChannelFeatureUpdateInput,
	EqualizerFilterFeatureUpdateInput
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapAnalogLevelToSysApi,
	mapChannelLoudspeakerToSpeakerTypeSysApi,
	mapDeviceEqualizerContourToSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { SpatialSeparation } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import {
	DspCopyChannel,
	DspCopyChannelProperty,
	DspDevice,
	DspDeviceChannelDspKind,
	DspDeviceMatrixmixer,
	DspEqualizerFilter
} from '../../api/models';
import { ContextMenuApiService } from '../../api/services';
import {
	AecMonoReferenceMutationGQL,
	AecStereoReferenceMutationGQL,
	UpdateChannelMutationGQL
} from '../graphql/generated/context-menu-channel.generated';
import { GetDeviceMatrixMixerQueryGQL, GetDeviceQueryGQL } from '../graphql/generated/context-menu-device.generated';
import { GetCopyChannelQueryGQL } from '../graphql/generated/copy-channel.generated';
import {
	mapChannelInfoDeviceFromSysApi,
	mapContextMenuCopyChannelFromSysApi,
	mapDurationMilliesToISO,
	mapMatrixmixer
} from '../mappers';

import { DaiChannelInfoSubscriptionsService } from './';

@Injectable()
export class DaiContextMenuStateApiService implements ContextMenuApiService {
	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		private readonly getDeviceQueryGQL: GetDeviceQueryGQL,
		private readonly getCopyChannelQueryGQL: GetCopyChannelQueryGQL,
		private readonly getDeviceMatrixMixerQueryGQL: GetDeviceMatrixMixerQueryGQL,
		private readonly updateChannelMutationGQL: UpdateChannelMutationGQL,
		private readonly subscriptions: DaiChannelInfoSubscriptionsService,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly aecMonoReferenceMutationGQL: AecMonoReferenceMutationGQL,
		private readonly aecStereoReferenceMutationGQL: AecStereoReferenceMutationGQL
	) {
		this.logger = logger.createScopedLogger('DaiContextMenuStateApiService');
	}

	public getDevice$(deviceId: string): Observable<DspDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Querying device', { queryId, deviceId });
		return combineLatest([
			this.getDeviceQueryGQL.watch({ id: deviceId }, { errorPolicy: 'all', fetchPolicy: 'cache-and-network' })
				.valueChanges,
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId)
		]).pipe(
			map(([query, auth]) => {
				if (!query.data?.node || !('isDeviceNode' in query.data.node)) {
					return undefined;
				}

				const device = mapChannelInfoDeviceFromSysApi(query.data.node, auth.authenticated, auth.readonlyMode);
				this.logger.trace('getDevice$()', 'Query updated', { device });
				return device;
			}),
			filter(
				(device): device is DspDevice & { equalizerFilterIds: string[] } =>
					device !== undefined && !!device.features.audioChannels
			),
			throttleTime(1000, asyncScheduler, { leading: true, trailing: true }),
			tapOnce((device) => {
				this.subscriptions.device.subscribe(queryId, device?.id);
			}),
			tap((device) => {
				if (device?.equalizerFilterIds.length) {
					this.subscriptions.equalizer.subscribeToOnly(queryId, device?.equalizerFilterIds);
				}
			}),
			noMoreSubscribers(() => {
				this.subscriptions.device.unsubscribeQuery(queryId);
				this.subscriptions.equalizer.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query device', {
					deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public getCopyChannel(channelId: string): Observable<DspCopyChannel> {
		this.logger.trace('getCopyChannel()', 'Querying channel for copying', { channelId });
		return this.getCopyChannelQueryGQL.fetch({ id: channelId }, { errorPolicy: 'all' }).pipe(
			map((query) => {
				if (!query.data?.node || !('isAudioCopyChannelNode' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const channel = mapContextMenuCopyChannelFromSysApi(query.data.node);
				this.logger.trace('getCopyChannel()', 'Query done', { channel });
				return channel;
			}),
			catchError((error) => {
				this.logger.error('getCopyChannel()', 'Failed to query channel for copying', {
					channelId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public getMatrixMixer$(deviceId: string): Observable<DspDeviceMatrixmixer> {
		const queryId = uuidv4();
		let crossPointIdsToSubscribe: string[];
		this.logger.trace('getMatrixMixer$()', 'Querying device matrtix mixer', { queryId, deviceId });
		return this.getDeviceMatrixMixerQueryGQL.watch({ id: deviceId }, { errorPolicy: 'all' }).valueChanges.pipe(
			map((query) => {
				if (!query.data?.node || !('isDeviceNode' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const [matrixMixer, crosspointIds] = mapMatrixmixer(query.data.node);
				crossPointIdsToSubscribe = crosspointIds;
				this.logger.trace('getMatrixMixer$()', 'Query updated', { matrixMixer });

				return matrixMixer;
			}),
			tapOnce(() => {
				this.subscriptions.crosspoints.subscribeToOnly(queryId, crossPointIdsToSubscribe);
			}),
			noMoreSubscribers(() => {
				this.subscriptions.crosspoints.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getMatrixMixer$()', 'Failed to query device matrix mixer', {
					deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public pasteToChannels(
		source: DspCopyChannel,
		targetIds: string[],
		properties: DspCopyChannelProperty[]
	): Observable<void> {
		this.logger.trace('pasteToChannels()', 'Pasting channel settings', { source, targetIds, properties });
		return forkJoin(targetIds.map((channelId) => this.getCopyChannel(channelId))).pipe(
			switchMap((targetChannels) => {
				const updates = properties
					.map((property) =>
						targetChannels
							.map((target) => this.mapCopyPropertyToNodeInputs(source, target, property))
							.flat()
					)
					.flat();

				this.logger.trace('pasteToChannels()', 'Sending mutation', { updates });
				return this.mutateChannels(updates);
			}),
			catchError((error) => {
				this.logger.error('pasteToChannels()', 'Failed to paste settings to channels', {
					source,
					targetIds,
					properties,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public pasteToAecMonoReferenceChannels(referenceChannelId: string, targetIds: string[]): Observable<void[]> {
		this.logger.trace('pasteToAecMonoReferenceChannels()', 'Pasting AEC Mono Reference channel ID', {
			referenceChannelId,
			targetIds
		});
		const mutationResults$ = targetIds.map((targetId) => {
			this.logger.trace('pasteToAecMonoReferenceChannels()', 'Sending mutation', { targetId });
			return this.mutateAecMonoReferenceChannel(targetId, referenceChannelId).pipe(
				catchError((error) => {
					this.logger.error(
						'pasteToAecMonoReferenceChannels()',
						'Failed to paste AEC Mono Reference channel ID to channel',
						{
							targetId,
							error
						}
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
		});
		return forkJoin(mutationResults$);
	}

	public pasteToAecStereoReferenceChannels(
		stereoReference: { selectedLeftReferenceSourceId: string; selectedRightReferenceSourceId: string },
		targetIds: string[]
	): Observable<void[]> {
		this.logger.trace('pasteToAecStereoReferenceChannels()', 'Pasting AEC Stereo Reference channel IDs', {
			stereoReference,
			targetIds
		});
		const leftMutationResults$ = targetIds.map((targetId) => {
			this.logger.trace('pasteToAecStereoReferenceChannels()', 'Sending mutation', { targetId });
			return this.mutateAecStereoReferenceChannel(
				targetId,
				stereoReference.selectedLeftReferenceSourceId,
				SpatialSeparation.Left
			).pipe(
				catchError((error) => {
					this.logger.error(
						'pasteToAecStereoReferenceChannels()',
						'Failed to paste AEC Stereo Reference Left channel ID to channel',
						{
							targetId,
							error
						}
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
		});
		const righttMutationResults$ = targetIds.map((targetId) => {
			this.logger.trace('pasteToAecStereoReferenceChannels()', 'Sending mutation', { targetId });
			return this.mutateAecStereoReferenceChannel(
				targetId,
				stereoReference.selectedRightReferenceSourceId,
				SpatialSeparation.Right
			).pipe(
				catchError((error) => {
					this.logger.error(
						'pasteToAecStereoReferenceChannels()',
						'Failed to paste AEC Stereo Reference Right channel ID to channel',
						{
							targetId,
							error
						}
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
		});
		return forkJoin([...leftMutationResults$, ...righttMutationResults$]);
	}

	/**
	 * Perform channel mutation.
	 */
	private mutateChannels(updates: NodeUpdateInput[]): Observable<void> {
		this.logger.trace('mutateChannel()', 'Updating channel', { updates });
		return this.updateChannelMutationGQL
			.mutate({ updates, injectKeyring: '//supplied-by-keyring-injector' }, { errorPolicy: 'all' })
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						throw result.data.updateNodes[0].error.message;
					}

					this.logger.trace('mutateChannel()', 'Done');
					return undefined;
				}),
				catchError((error: Error) => {
					this.logger.error('mutateChannel()', 'Error', { error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	private mutateAecMonoReferenceChannel(channelId: string, referenceChannelId: string): Observable<void> {
		this.logger.trace('mutateAecMonoReferenceChannel()', 'Updating AEC mono reference channel', {
			channelId,
			referenceChannelId
		});

		return this.aecMonoReferenceMutationGQL
			.mutate(
				{
					input: {
						id: channelId,
						source: referenceChannelId
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateAcousticEchoCancellationMonoReferenceSource &&
						'error' in mutation.data.updateAcousticEchoCancellationMonoReferenceSource &&
						mutation.data.updateAcousticEchoCancellationMonoReferenceSource.error !== null
					) {
						this.logger.error(
							'mutateAecMonoReferenceChannel()',
							'Failed to update AEC mono reference channel',
							{
								channelId,
								mutation
							}
						);
						throw mutation.data?.updateAcousticEchoCancellationMonoReferenceSource.error;
					}

					this.logger.trace('mutateAecMonoReferenceChannel()', 'Done', { channelId });
					return undefined;
				}),
				catchError((error) => {
					this.logger.error(
						'mutateAecMonoReferenceChannel()',
						'Failed to update AEC mono reference channel',
						{ error }
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	private mutateAecStereoReferenceChannel(
		channelId: string,
		referenceChannelId: string,
		spatialSeparation: SpatialSeparation
	): Observable<void> {
		this.logger.trace('mutateAecStereoReferenceChannel()', 'Updating AEC Stereo reference channel', {
			channelId,
			referenceChannelId,
			spatialSeparation
		});

		return this.aecStereoReferenceMutationGQL
			.mutate(
				{
					input: {
						id: channelId,
						source: referenceChannelId,
						stereoAudioChannelSpatialSeparation: spatialSeparation
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateAcousticEchoCancellationStereoReferenceSource &&
						'error' in mutation.data.updateAcousticEchoCancellationStereoReferenceSource &&
						mutation.data.updateAcousticEchoCancellationStereoReferenceSource.error !== null
					) {
						this.logger.error(
							'mutateAecStereoReferenceChannel()',
							'Failed to update AEC Stereo reference channel',
							{
								channelId,
								mutation
							}
						);
						throw mutation.data?.updateAcousticEchoCancellationStereoReferenceSource.error;
					}

					this.logger.trace('mutateAecStereoReferenceChannel()', 'Done', { channelId });
					return undefined;
				}),
				catchError((error) => {
					this.logger.error(
						'mutateAecStereoReferenceChannel()',
						'Failed to update AEC Stereo reference channel',
						{
							error
						}
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	private mapCopyPropertyToNodeInputs(
		source: DspCopyChannel,
		target: DspCopyChannel,
		property: DspCopyChannelProperty
	): NodeUpdateInput[] {
		switch (property) {
			case 'autoMute':
				return source.features.autoMute !== null
					? this.toAudioChannelFeatureUpdates(target.id, {
							autoMute: { enabled: source.features.autoMute }
					  })
					: [];
			case 'muted':
				return this.toAudioChannelFeatureUpdates(target.id, { mute: { muted: source.features.muted } });
			case 'gain':
				return this.toAudioChannelFeatureUpdates(target.id, { gain: { gain: source.features.gain } });
			case 'delay':
				return this.toAudioChannelFeatureUpdates(target.id, {
					delay: {
						enabled: source.features.delay?.enabled,
						delay: mapDurationMilliesToISO(source.features.delay?.delay)
					}
				});
			case 'automaticGainControl':
				return this.toAudioChannelFeatureUpdates(target.id, {
					automaticGainControl: { ...source.features.automaticGainControl }
				});
			case 'compressor':
				return this.toAudioChannelFeatureUpdates(target.id, { compressor: { ...source.features.compressor } });
			case 'denoiser':
				return this.toAudioChannelFeatureUpdates(target.id, { denoiser: { ...source.features.denoiser } });
			case 'deverb':
				return this.toAudioChannelFeatureUpdates(target.id, { deverb: { ...source.features.deverb } });
			case 'inputSource':
				return source.features.inputSource
					? this.toAudioChannelFeatureUpdates(target.id, {
							inputSource: { ...source.features.inputSource }
					  })
					: [];
			case 'limiter':
				return this.toAudioChannelFeatureUpdates(target.id, { limiter: { ...source.features.limiter } });
			case 'noiseReduction':
				return this.toAudioChannelFeatureUpdates(target.id, {
					noiseReduction: { ...source.features.noiseReduction }
				});
			case 'outputPath':
				return source.features.outputPath
					? this.toAudioChannelFeatureUpdates(target.id, { outputPath: { ...source.features.outputPath } })
					: [];

			case 'passiveSpeakers':
				return source.features.passiveSpeakers
					? this.toAudioChannelFeatureUpdates(target.id, {
							passiveSpeakers: {
								type: mapChannelLoudspeakerToSpeakerTypeSysApi(source.features.passiveSpeakers.type)
							}
					  })
					: [];

			case 'analogLevel':
				return this.mapAnalogLevelCopyToNodeInputs(source, target);
			case 'equalizer':
				return this.mapEqualizerCopyToNodeInputs(source, target);
			case 'deviceSpecificEqualizerContour':
				return this.mapDeviceSpecificEqualizerContourNodeInputs(source, target);

			case 'acousticEchoCancellation':
				return this.mapAcousticEchoCancellationCopyToNodeInputs(source, target);
			case 'pcPeripheral':
				return source.features.pcPeripheral
					? this.toAudioChannelFeatureUpdates(target.id, {
							pcPeripheral: {
								selectedPeripheral: { id: source.features.pcPeripheral.selectedPeripheralId }
							}
					  })
					: [];
		}
	}

	private toAudioChannelFeatureUpdates(
		channelId: string,
		features: AudioChannelFeatureUpdateInput
	): NodeUpdateInput[] {
		return [{ audioChannel: { id: channelId, features } }];
	}

	private mapAnalogLevelCopyToNodeInputs(source: DspCopyChannel, target: DspCopyChannel): NodeUpdateInput[] {
		if (!source.features.analogLevel || !target.features.analogLevel) {
			return [];
		}

		return [
			{
				audioChannel: {
					id: target.id,
					features: { analogLevel: { level: mapAnalogLevelToSysApi(source.features.analogLevel) } }
				}
			}
		];
	}

	private mapEqualizerCopyToNodeInputs(source: DspCopyChannel, target: DspCopyChannel): NodeUpdateInput[] {
		const sourceEq = source.features.equalizer;
		const targetEq = target.features.equalizer;
		if (!sourceEq || !targetEq) {
			return [];
		}

		const updates: NodeUpdateInput[] = (target.features.equalizer?.filters || [])
			.map((filter, index) => this.mapEqualizerFilterCopyToNodeInputs(sourceEq.filters[index], filter))
			.flat();

		return updates;
	}

	private mapEqualizerFilterCopyToNodeInputs(
		sourceFilter: DspEqualizerFilter,
		targetFilter: DspEqualizerFilter
	): NodeUpdateInput[] {
		const updates: Array<EqualizerFilterFeatureUpdateInput> = [
			{
				configuration: {
					enabled: sourceFilter.features.configuration.enabled,
					filterType: sourceFilter.features.configuration.filterType
				}
			},
			{ frequency: { frequency: sourceFilter.features.frequency } },
			{ gain: { gain: sourceFilter.features.gain } },
			{ qFactor: { qFactor: sourceFilter.features.qFactor } }
		];
		return updates.map((up) => ({ equalizerFilters: { id: targetFilter.id, features: up } }));
	}

	private mapAcousticEchoCancellationCopyToNodeInputs(
		source: DspCopyChannel,
		target: DspCopyChannel
	): NodeUpdateInput[] {
		return [
			{
				audioChannel: {
					id: target.id,
					features: {
						acousticEchoCancellation: {
							enabled: source.features.acousticEchoCancellation?.enabled,
							nonLinearProcessingLevel: source.features.acousticEchoCancellation?.nonLinearProcessingLevel
						}
					}
				}
			}
		];
	}

	private mapDeviceSpecificEqualizerContourNodeInputs(
		source: DspCopyChannel,
		target: DspCopyChannel
	): NodeUpdateInput[] {
		const sourceDeviceSpecificEqualizerContour = source.features.deviceSpecificEqualizerContour;
		const targetDeviceSpecificEqualizerContour = target.features.deviceSpecificEqualizerContour;
		if (!sourceDeviceSpecificEqualizerContour || !targetDeviceSpecificEqualizerContour) {
			return [];
		}

		return [
			{
				audioChannel: {
					id: target.id,
					features: {
						deviceSpecificEqualizerContour: {
							deviceSpecificEqualizerContour: mapDeviceEqualizerContourToSysApi(
								sourceDeviceSpecificEqualizerContour.deviceSpecificEqualizerContour
							)
						}
					}
				}
			}
		];
	}

	private dspKindToAudioChannelFeatureUpdate(
		kind: DspDeviceChannelDspKind,
		enabled: boolean
	): AudioChannelFeatureUpdateInput[] {
		switch (kind) {
			case 'acousticEchoCancellation':
				return [{ acousticEchoCancellation: { enabled } }];
			case 'automaticGainControl':
				return [{ automaticGainControl: { enabled } }];
			case 'compressor':
				return [{ compressor: { enabled } }];
			case 'delay':
				return [{ delay: { enabled } }];
			case 'equalizer':
				return [{ equalizer: { enabled } }];
			case 'limiter':
				return [{ limiter: { enabled } }];
			case 'noiseReduction':
				return [{ noiseReduction: { enabled } }];
			case 'denoiser':
				return [{ denoiser: { enabled } }];
			case 'deverb':
				return [{ deverb: { enabled } }];
		}
	}
}
